import { useMatches, useNavigate } from "@tanstack/react-router"
import React, { useEffect, useRef, useState } from "react"
import { Tooltip } from "~/components/reusable/Tooltip"
import { type FileRouteTypes } from "~/routeTree.gen"

import { cn } from "~/utils/helpers"
import { Badge, type BadgeVariant } from "~/components/reusable/Badge"
import { formatNumber } from "~/utils/formatting"
import { HAKIO_ONLY_TOOLTIP } from "~/constants/hakioOnly"

export interface HeaderTab {
  label: string
  path?: FileRouteTypes["to"] // TODO: Make this required when all pages refactored
  icon?: React.ReactNode
  disabled?: boolean
  tooltip?: string
  hakioOnly?: boolean
  count?: number
  countVariant?: BadgeVariant
}

/** The tab whose path is the current route, else the first tab. */
export const getActiveTab = (tabs: HeaderTab[], fullPath: string | undefined) =>
  tabs.find((x) => x.path === fullPath) ?? tabs[0]

interface HeaderTabsProps {
  tabs: HeaderTab[]
  selectedTab?: string // TODO: Remove this when all pages refactored
  containerClassName?: string
  headerClassName?: string
  onChange?: (selectedTab: string) => void
  path?: FileRouteTypes["to"] // TODO: Make this required when all pages refactored
}

const HeaderTabs: React.FC<HeaderTabsProps> = ({
  tabs,
  selectedTab, // TODO: Remove this when all pages refactored
  onChange,
  headerClassName,
  containerClassName,
}) => {
  const [indicatorStyle, setIndicatorStyle] = useState({})
  const matches = useMatches()
  const currentMatch = matches[matches.length - 1]

  const tabRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const [currentTab, setCurrentTab] = useState(tabs[0]?.label)
  const navigate = useNavigate()

  const handleTabClick = (newTab: string) => {
    if (!selectedTab) {
      // TODO: This should always run, when everything is refactored
      const tab = tabs.find((x) => x.label === newTab)
      if (!tab) return
      // The active tab follows the route (effect below), not the click: a blocked navigation
      // (unsaved-changes guard) must leave the old tab highlighted.
      navigate({
        to: tab.path as FileRouteTypes["to"],
        // `view` is dropped: a view id names a view on one table, not on a sibling tab's table.
        search: ({ view: _view, ...prev }: Record<string, unknown>) => ({ ...prev }),
      })
      onChange?.(newTab)
      return
    }

    onChange?.(newTab)
    const activeTab = tabRefs.current[newTab]
    if (activeTab) {
      setIndicatorStyle({
        width: `${activeTab.offsetWidth}px`,
        transform: `translateX(calc(${activeTab.offsetLeft}px - 16px))`,
      })
    }
  }

  useEffect(() => {
    // TODO: Remove this when all pages refactored
    if (selectedTab) {
      const activeTab = tabRefs.current[selectedTab]
      setCurrentTab(selectedTab)
      setIndicatorStyle({
        width: `${activeTab?.offsetWidth}px`,
        transform: `translateX(calc(${activeTab?.offsetLeft}px - 16px))`,
      })
      return
    }

    // A nested route (a tab with its own sub-routes) keeps its tab active
    // through any ancestor match. The deepest one wins: an Overview tab on the
    // module's own path is an ancestor of every other tab's routes too.
    const newSelectedTab =
      tabs.find((x) => x.path === currentMatch?.fullPath) ??
      tabs
        .filter((x) => matches.some((match) => match.fullPath === x.path))
        .reduce<
          HeaderTab | undefined
        >((best, x) => ((x.path?.length ?? 0) > (best?.path?.length ?? -1) ? x : best), undefined) ??
      tabs[0]
    if (newSelectedTab) {
      const activeTab = tabRefs.current[newSelectedTab.label]
      setCurrentTab(newSelectedTab.label)
      setIndicatorStyle({
        width: `${activeTab?.offsetWidth}px`,
        transform: `translateX(calc(${activeTab?.offsetLeft}px - 16px))`,
      })
    }
  }, [matches, tabs, selectedTab])

  return (
    <div
      className={cn(
        "relative flex gap-5 px-4 w-full border-b border-base overflow-x-auto hide-scrollbar",
        containerClassName,
      )}
    >
      <div
        className="absolute bottom-0 h-0.5 bg-purpleheart-500 transition-transform duration-300 ease-out"
        style={indicatorStyle}
      />
      {tabs.map((tab) => (
        <Tooltip key={tab.label} title={tab.hakioOnly ? HAKIO_ONLY_TOOLTIP : tab.tooltip}>
          <div
            key={tab.label}
            ref={(el) => {
              tabRefs.current[tab.label] = el
            }}
            className={cn(
              "cursor-pointer py-3 label-base text-fg-subtle flex flex-row gap-1.5 items-center shrink-0 whitespace-nowrap",
              {
                "text-fg-base": currentTab === tab.label,
              },
              tab.disabled && "text-fg-disabled cursor-not-allowed",
              headerClassName,
              tab.hakioOnly && "[&_svg]:fill-orange-600 text-orange-600",
            )}
            onClick={() => {
              if (tab.disabled) return
              handleTabClick(tab.label)
            }}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <Badge variant={tab.countVariant ?? "neutral"} size="xs">
                {formatNumber(tab.count)}
              </Badge>
            )}
          </div>
        </Tooltip>
      ))}
    </div>
  )
}

export default HeaderTabs
